import { useState } from 'react';
import './SellerHelp.css';
import { useTransition } from '../context/TransitionContext';
import { useLanguage } from '../context/LanguageContext';

const SellerHelp = () => {
  const { startTransition } = useTransition();
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [selectedRating, setSelectedRating] = useState(null);

  const categories = [
    { id: 'all', name: t('sellerHelp.allTopics'), icon: null, iconText: 'All', count: 7 },
    { id: 'development', name: t('sellerHelp.appDevelopment'), icon: null, iconText: 'Dev', count: 3 },
    { id: 'business', name: t('sellerHelp.businessPayment'), icon: null, iconText: 'Biz', count: 2 },
    { id: 'support', name: t('sellerHelp.supportMaintenance'), icon: null, iconText: 'Sup', count: 2 }
  ];

  const faqs = [
    {
      id: 1,
      category: 'development',
      question: 'What kind of apps do you develop?',
      answer: 'I develop mobile and web applications ranging from simple utilities to complex business solutions, focusing on quality and usability.',
      tags: ['development', 'mobile', 'web', 'business']
    },
    {
      id: 2,
      category: 'business',
      question: 'How can I contact you for custom app development?',
      answer: 'You can reach me via email at qalvinahmad@gmail.com or through the contact form on my website.',
      tags: ['contact', 'custom', 'development', 'email']
    },
    {
      id: 3,
      category: 'support',
      question: 'Do you offer maintenance and updates?',
      answer: 'Yes, maintenance and app updates are available under separate agreements.',
      tags: ['maintenance', 'updates', 'support', 'agreement']
    },
    {
      id: 4,
      category: 'business',
      question: 'How do you handle payment and refunds?',
      answer: 'Payments are usually made in milestones, with an initial deposit required before work begins. Refunds are considered on a case-by-case basis, particularly if the project is canceled before significant work starts.',
      tags: ['payment', 'refunds', 'milestones', 'deposit']
    },
    {
      id: 5,
      category: 'development',
      question: 'What platforms do you develop for?',
      answer: 'I develop apps for Android, iOS, and web platforms.',
      tags: ['platforms', 'android', 'ios', 'web']
    },
    {
      id: 6,
      category: 'development',
      question: 'How secure are the applications you develop?',
      answer: 'I prioritize security by implementing industry best practices such as secure coding standards, data encryption, proper authentication mechanisms, and regular testing to minimize vulnerabilities and protect user data.',
      tags: ['security', 'encryption', 'authentication', 'testing']
    },
    {
      id: 7,
      category: 'support',
      question: 'How can I provide feedback or suggestions to you?',
      answer: 'You can send your feedback or suggestions anytime via email at qalvinahmad@gmail.com or through the contact form on my website. I highly appreciate user input to improve my services and applications.',
      tags: ['feedback', 'suggestions', 'contact', 'improvement']
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const quickActions = [
    {
      title: t('sellerHelp.startProject'),
      description: t('sellerHelp.startProjectDesc'),
      icon: null,
      iconText: '->',
      action: 'primary',
      link: '/contact'
    },
    {
      title: t('sellerHelp.viewPortfolio'),
      description: t('sellerHelp.viewPortfolioDesc'),
      icon: null,
      iconText: '[]',
      action: 'secondary',
      link: '/work'
    },
    {
      title: t('sellerHelp.technicalDocs'),
      description: t('sellerHelp.technicalDocsDesc'),
      icon: null,
      iconText: 'Doc',
      action: 'secondary',
      link: '/about'
    }
  ];

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Scroll to FAQ section
      const faqSection = document.querySelector('.faq-section');
      if (faqSection) {
        faqSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  return (
    <div className="seller-help">
      {/* Feedback Toast Message */}
      {feedbackMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          padding: '16px 24px',
          backgroundColor: 'var(--v2-color-charcoal)',
          color: 'white',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: 500,
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideUp 0.3s ease'
        }}>
          {feedbackMessage}
        </div>
      )}

      {/* Hero Section with Enhanced Visual Hierarchy */}
      <section className="help-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">S</span>
              <span>{t('sellerHelp.devSupport')}</span>
            </div>
            <h1 className="hero-title">
              {t('sellerHelp.title')}
            </h1>
            <p className="hero-subtitle">
              {t('sellerHelp.subtitle')}
            </p>
            
            {/* Enhanced Search with Visual Feedback */}
            <div className="search-container">
              <div className="search-wrapper">
                <span className="search-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </span>
                <input
                  type="text"
                  placeholder={t('sellerHelp.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchSubmit}
                  className="search-input"
                />
                {searchQuery && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="search-suggestions">
                <span className="suggestion-label">{t('sellerHelp.popular')}:</span>
                {['app development', 'payment', 'platforms'].map(suggestion => (
                  <button
                    key={suggestion}
                    className="suggestion-chip"
                    onClick={() => setSearchQuery(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="quick-actions-section">
        <div className="container">
          <div className="quick-actions-grid">
            {quickActions.map((action, index) => (
              <button
                key={index}
                className={`quick-action-card ${action.action}`}
                onClick={() => startTransition(action.link)}
              >
                <span className="quick-action-icon">
                  {action.icon ? action.icon : <span style={{fontSize: '14px', fontWeight: 'bold'}}>{action.iconText}</span>}
                </span>
                <h3 className="quick-action-title">{action.title}</h3>
                <p className="quick-action-description">{action.description}</p>
                <span className="quick-action-arrow">→</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="container">
    

        {/* Category Navigation with Cognitive Load Management */}
        <section className="help-categories-section">
          <div className="categories-header">
            <h2 className="section-title">{t('sellerHelp.browseByCategory')}</h2>
            <p className="section-subtitle">{t('sellerHelp.categorySubtitle')}</p>
          </div>
          
          <div className="categories-nav">
            {categories.map(category => (
              <button
                key={category.id}
                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <span className="category-icon">
                  {category.icon ? category.icon : <span style={{fontSize: '12px', fontWeight: 'bold'}}>{category.iconText}</span>}
                </span>
                <span className="category-name">{category.name}</span>
                <span className="category-count">{category.count}</span>
              </button>
            ))}
          </div>
        </section>

        {/* FAQ Section with Enhanced UX Patterns */}
        <section className="faq-section">
          <div className="faq-header">
            <h2 className="section-title">
              {activeCategory === 'all' ? t('sellerHelp.allQuestions') : 
               categories.find(c => c.id === activeCategory)?.name}
            </h2>
            <div className="results-info">
              {filteredFaqs.length} {filteredFaqs.length === 1 ? t('sellerHelp.result') : t('sellerHelp.results')} {t('sellerHelp.found')}
            </div>
          </div>

          <div className="faq-list">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map(faq => (
                <div key={faq.id} className={`faq-item ${expandedFaq === faq.id ? 'expanded' : ''}`}>
                  <button
                    className="faq-question"
                    onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                    aria-expanded={expandedFaq === faq.id}
                  >
                    <h3 className="question-text">{faq.question}</h3>
                    <span className="expand-icon">
                      {expandedFaq === faq.id ? '−' : '+'}
                    </span>
                  </button>
                  
                  <div className="faq-answer">
                    <div className="answer-content">
                      <p>{faq.answer}</p>
                      <div className="faq-tags">
                        {faq.tags.map(tag => (
                          <span key={tag} className="faq-tag">{tag}</span>
                        ))}
                      </div>
                      <div className="faq-actions">
                        <button 
                          className="helpful-btn"
                          onClick={() => {
                            setFeedbackMessage(t('sellerHelp.thankFeedback'));
                            setTimeout(() => setFeedbackMessage(''), 3000);
                          }}
                        >
                          {t('sellerHelp.helpful')}
                        </button>
                        <button 
                          className="feedback-btn"
                          onClick={() => startTransition('/contact')}
                        >
                          {t('sellerHelp.needMoreHelp')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <div className="no-results-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
                <h3>{t('sellerHelp.noResults')}</h3>
                <p>{t('sellerHelp.noResultsDesc')}</p>
                <button 
                  className="reset-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('all');
                  }}
                >
                  {t('sellerHelp.resetFilters')}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Enhanced Contact Support with Better UX Writing */}
        <section className="contact-support-section">
          <div className="support-header">
            <h2 className="section-title">{t('sellerHelp.stillNeedHelp')}</h2>
            <p className="section-subtitle">{t('sellerHelp.supportSubtitle')}</p>
          </div>
          
          <div className="support-options">
            <div className="support-card priority">
              <div className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <h3 className="support-title">{t('sellerHelp.directContact')}</h3>
              <p className="support-description">{t('sellerHelp.contactDesc')}</p>
              <div className="support-meta">
                <span className="response-time">{t('sellerHelp.responseWithin24')}</span>
              </div>
              <a href="mailto:qalvinahmad@gmail.com" className="support-btn primary">
                {t('sellerHelp.sendEmail')}
                <span className="btn-icon">→</span>
              </a>
            </div>
            
            <div className="support-card">
              <div className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <h3 className="support-title">{t('sellerHelp.projectConsultation')}</h3>
              <p className="support-description">{t('sellerHelp.consultationDesc')}</p>
              <div className="support-meta">
                <span className="community-size">{t('sellerHelp.freeConsultation')}</span>
              </div>
              <a 
                href="https://instagram.com/qalvinahmad" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="support-btn secondary"
              >
                {t('sellerHelp.message')}
                <span className="btn-icon">→</span>
              </a>
            </div>
          </div>

          {/* Enhanced Feedback Section */}
          <div className="feedback-section">
            <div className="feedback-card">
              <h3>{t('sellerHelp.rateExperience')}</h3>
              <div className="rating-buttons">
                {[1, 2, 3, 4, 5].map(rating => (
                  <button 
                    key={rating} 
                    className={`rating-btn ${selectedRating === rating ? 'selected' : ''}`}
                    title={`${rating}/5`}
                    onClick={() => {
                      setSelectedRating(rating);
                      setFeedbackMessage(`${t('sellerHelp.thankRating')} ${rating} ${t('sellerHelp.stars')}`);
                      setTimeout(() => setFeedbackMessage(''), 3000);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill={selectedRating === rating ? '#FF9800' : 'none'} stroke="#FF9800" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  </button>
                ))}
              </div>
              <p className="feedback-subtitle">{t('sellerHelp.feedbackSubtitle')}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SellerHelp;
